import { useState, useCallback, useRef } from 'react'

// Grid size for snap
const GRID = 20
const g = (v) => Math.round(v / GRID) * GRID

// Static defaults -- grid-aligned, designed for ~1520x900
const DEFAULTS = {
  navbar:   { visible: true,  x: 20,   y: 300, width: 80,  height: 560, zIndex: 1 },
  topbar:   { visible: true,  x: 460,  y: 60,  width: 600, height: 60,  zIndex: 10 },
  sidebar:  { visible: true,  x: 100,  y: 160, width: 360, height: 760, zIndex: 3 },
  messages: { visible: true,  x: 460,  y: 160, width: 800, height: 880, zIndex: 4 },
  thread:   { visible: false, x: 1080, y: 100, width: 380, height: 980, zIndex: 5 },
  zoom:     { visible: true,  x: 0,    y: 0,   width: 0,   height: 0,   zIndex: 0 },
}

const STORAGE_KEY = 'paradigm_module_layout'
const SAVED_LAYOUTS_KEY = 'paradigm_saved_layouts'

function loadLayout() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      // Use saved values as-is, only add missing modules from defaults
      const result = {}
      for (const id of Object.keys(DEFAULTS)) {
        result[id] = parsed[id] || { ...DEFAULTS[id] }
      }
      return result
    }
  } catch (e) {}
  return structuredClone(DEFAULTS)
}

function saveLayout(layout) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout))
  } catch (e) {}
}

function loadSavedLayouts() {
  try {
    const raw = localStorage.getItem(SAVED_LAYOUTS_KEY)
    if (raw) return JSON.parse(raw)
  } catch (e) {}
  return []
}

function persistSavedLayouts(layouts) {
  try {
    localStorage.setItem(SAVED_LAYOUTS_KEY, JSON.stringify(layouts))
  } catch (e) {}
}

export default function useModuleLayout() {
  const [layout, setLayout] = useState(loadLayout)
  const [savedLayouts, setSavedLayouts] = useState(loadSavedLayouts)
  const [snapToGrid, setSnapToGrid] = useState(() => {
    try { return localStorage.getItem('paradigm_snap_grid') === 'true' } catch { return false }
  })
  const toggleSnapToGrid = useCallback(() => {
    setSnapToGrid(prev => {
      const next = !prev
      localStorage.setItem('paradigm_snap_grid', String(next))
      return next
    })
  }, [])
  const zCounterRef = useRef(
    Math.max(...Object.values(loadLayout()).map(m => m.zIndex || 0), 10)
  )

  const updateLayout = useCallback((updater) => {
    setLayout(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      saveLayout(next)
      return next
    })
  }, [])

  const toggleModule = useCallback((id) => {
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], visible: !prev[id]?.visible }
    }))
  }, [updateLayout])

  const closeModule = useCallback((id) => {
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], visible: false }
    }))
  }, [updateLayout])

  const openModule = useCallback((id) => {
    zCounterRef.current += 1
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], visible: true, zIndex: zCounterRef.current }
    }))
  }, [updateLayout])

  const moveModule = useCallback((id, x, y) => {
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], x, y }
    }))
  }, [updateLayout])

  const resizeModule = useCallback((id, width, height) => {
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], width, height }
    }))
  }, [updateLayout])

  const focusModule = useCallback((id) => {
    zCounterRef.current += 1
    updateLayout(prev => ({
      ...prev,
      [id]: { ...prev[id], zIndex: zCounterRef.current }
    }))
  }, [updateLayout])

  // Version counter -- increments on reset/load to force remount of ModuleShells
  const [layoutVersion, setLayoutVersion] = useState(0)

  // Reset to default layout
  const resetLayout = useCallback(() => {
    const defaults = structuredClone(DEFAULTS)
    zCounterRef.current = 10
    setLayout(defaults)
    saveLayout(defaults)
    setLayoutVersion(v => v + 1)
  }, [])

  // Save current layout as a named snapshot
  const saveLayoutSnapshot = useCallback((name) => {
    const snapshot = { name, layout: structuredClone(layout), timestamp: Date.now() }
    const updated = [...savedLayouts, snapshot]
    setSavedLayouts(updated)
    persistSavedLayouts(updated)
  }, [layout, savedLayouts])

  // Load a saved layout snapshot
  const loadLayoutSnapshot = useCallback((index) => {
    const snapshot = savedLayouts[index]
    if (!snapshot) return
    setLayoutVersion(v => v + 1)
    const merged = {}
    for (const id of Object.keys(DEFAULTS)) {
      merged[id] = { ...DEFAULTS[id], ...(snapshot.layout[id] || {}) }
    }
    zCounterRef.current = Math.max(...Object.values(merged).map(m => m.zIndex || 0), 10)
    setLayout(merged)
    saveLayout(merged)
  }, [savedLayouts])

  // Delete a saved layout
  const deleteLayoutSnapshot = useCallback((index) => {
    const updated = savedLayouts.filter((_, i) => i !== index)
    setSavedLayouts(updated)
    persistSavedLayouts(updated)
  }, [savedLayouts])

  const getModuleProps = useCallback((id) => {
    const mod = layout[id] || DEFAULTS[id] || {}
    return {
      id,
      defaultX: mod.x,
      defaultY: mod.y,
      defaultWidth: mod.width,
      defaultHeight: mod.height,
      zIndex: mod.zIndex || 1,
      visible: mod.visible !== false,
      onClose: () => closeModule(id),
      onFocus: () => focusModule(id),
      onMove: (x, y) => moveModule(id, x, y),
      onResize: (w, h) => resizeModule(id, w, h),
      snapToGrid,
    }
  }, [layout, closeModule, focusModule, moveModule, resizeModule, snapToGrid])

  return {
    layout,
    toggleModule,
    closeModule,
    openModule,
    moveModule,
    focusModule,
    resizeModule,
    resetLayout,
    savedLayouts,
    saveLayoutSnapshot,
    loadLayoutSnapshot,
    deleteLayoutSnapshot,
    snapToGrid,
    toggleSnapToGrid,
    layoutVersion,
    getModuleProps,
  }
}
